import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { ActionKind, BoardResponse, CaseView, Settings } from '@chemoflow/contracts';
import { CLERK_ACTION_LABEL } from '@chemoflow/domain';
import { ApiFailure, newRequestId } from '../api/client.js';
import { useCaseAction, useCaseUndo } from '../api/hooks.js';
import { surname } from '../lib/format.js';
import { useClock } from './clock.js';
import { useUi } from './ui.js';

interface BoardValue {
  cases: CaseView[];
  settings: Settings;
  degradedSince: number | null;
  updatedAt: number;
  /** 同じ姓の患者がいるか（要件 #14 の注意喚起）。 */
  hasSameSurname(c: CaseView): boolean;
  /** 成功したら更新後の案件を返す。取消の対象にそのまま使える。 */
  act(input: {
    case: CaseView;
    action: ActionKind;
    reason?: string;
    /** 呼び出し側が独自のトーストを出す場合に、既定のトーストを抑える。 */
    silent?: boolean;
  }): Promise<CaseView | null>;
  undo(target: CaseView): Promise<boolean>;
  /**
   * その案件の更新が飛んでいる間は true。
   * ボタンを止める範囲は案件単位にする。ボード全体を止めると、別の患者への入力まで待たされる。
   */
  isBusy(caseId: string): boolean;
}

const BoardContext = createContext<BoardValue | null>(null);

const ACTION_LABEL: Record<ActionKind, string> = {
  confirm: CLERK_ACTION_LABEL.confirm,
  change: CLERK_ACTION_LABEL.change,
  stop: CLERK_ACTION_LABEL.stop,
  pharm_start: '調製開始',
  stop_ack: '中止を確認',
  admin_start: '投与開始',
  pharm_done: '調製完了',
  admin_done: '投与完了',
};

export function BoardProvider({ data, children }: { data: BoardResponse; children: ReactNode }) {
  const { toast } = useUi();
  const { syncServerTime } = useClock();
  const action = useCaseAction();

  // 端末ごとの時計ずれで「経過◯分」が食い違わないよう、取得のたびに基準を合わせる
  useEffect(() => {
    syncServerTime(data.serverTime);
  }, [data.serverTime, syncServerTime]);
  const undoMutation = useCaseUndo();

  const duplicated = useMemo(() => {
    const seen = new Map<string, number>();
    for (const c of data.cases) {
      if (!c.patient) continue;
      const s = surname(c.patient.name);
      seen.set(s, (seen.get(s) ?? 0) + 1);
    }
    return new Set([...seen].filter(([, n]) => n > 1).map(([s]) => s));
  }, [data.cases]);

  const hasSameSurname = useCallback(
    (c: CaseView) => (c.patient ? duplicated.has(surname(c.patient.name)) : false),
    [duplicated],
  );

  /**
   * 飛んでいる更新の案件ID。
   * 描画用の state と、同じ描画の中で続けて押されても止められる ref の2本立てにする。
   */
  const inFlight = useRef<Set<string>>(new Set());
  const [busyIds, setBusyIds] = useState<readonly string[]>([]);

  const beginCase = useCallback((caseId: string): boolean => {
    if (inFlight.current.has(caseId)) return false;
    inFlight.current.add(caseId);
    setBusyIds((ids) => [...ids, caseId]);
    return true;
  }, []);

  const endCase = useCallback((caseId: string) => {
    inFlight.current.delete(caseId);
    setBusyIds((ids) => ids.filter((id) => id !== caseId));
  }, []);

  const isBusy = useCallback((caseId: string) => busyIds.includes(caseId), [busyIds]);

  /**
   * この端末が最後に確定させた版。
   * 競合が自分の直前の操作によるものか、本当に他の端末の操作かを見分ける。
   * 誰も触っていないのに「他の端末で状態が変わった」と出すと、現場が確認の電話を始めてしまう。
   */
  const ownVersion = useRef<Map<string, number>>(new Map());

  /** 失敗の扱いを1か所にまとめる。競合は最新状態を取り直し、同じ操作を再送しない。 */
  const handleFailure = useCallback(
    (err: unknown, label: string) => {
      if (err instanceof ApiFailure) {
        if (err.code === 'version_conflict') {
          const mine =
            err.current !== undefined &&
            ownVersion.current.get(err.current.caseId) === err.current.version;
          toast(
            mine ? (
              <>
                直前の操作が反映済みのため <b>{label}</b> を記録できませんでした
                <small>最新の状態に更新しました。他の端末の操作ではありません</small>
              </>
            ) : (
              <>
                他の端末で状態が変わったため <b>{label}</b> を記録できませんでした
                <small>最新の状態に更新しました。もう一度ご確認ください</small>
              </>
            ),
            { ms: 8000 },
          );
          return;
        }
        toast(
          <>
            <b>{label}</b> を記録できませんでした<small>{err.message}</small>
          </>,
          { ms: 8000 },
        );
        return;
      }
      toast(
        <>
          通信できませんでした<small>電話運用への切り替えをご検討ください</small>
        </>,
        { ms: 8000 },
      );
    },
    [toast],
  );

  const act = useCallback<BoardValue['act']>(
    async ({ case: target, action: kind, reason, silent }) => {
      const label = ACTION_LABEL[kind];
      // 連打の2回目。サーバは版で弾くが、利用者には自分のタップが競合として返るだけなので送らない
      if (!beginCase(target.caseId)) return null;
      try {
        const res = await action.mutateAsync({
          caseId: target.caseId,
          action: kind,
          reason,
          expectedVersion: target.version,
          requestId: newRequestId(),
        });
        ownVersion.current.set(res.case.caseId, res.case.version);
        const name = res.case.patient?.name ?? '患者';
        const id = res.case.patient?.patientId ?? res.case.surrogateKey;
        if (silent && !res.callRequired) return res.case;
        if (res.callRequired) {
          toast(
            <>
              {name}（{id}）に {label} を記録しました
              <small>
                {res.callRequired.reason}
                {res.callRequired.extension ? ` ― ${res.callRequired.extension.label} 内線 ` : ''}
                {res.callRequired.extension ? (
                  <a href={`tel:${res.callRequired.extension.no}`}>
                    {res.callRequired.extension.no}
                  </a>
                ) : null}
              </small>
            </>,
            { tone: 'call', ms: 15000 },
          );
        } else {
          toast(
            <>
              {name}（{id}）に <b>{label}</b> を記録しました
            </>,
          );
        }
        return res.case;
      } catch (err) {
        handleFailure(err, label);
        return null;
      } finally {
        endCase(target.caseId);
      }
    },
    [action, handleFailure, toast, beginCase, endCase],
  );

  const undo = useCallback<BoardValue['undo']>(
    async (target) => {
      if (!beginCase(target.caseId)) return false;
      try {
        const res = await undoMutation.mutateAsync({
          caseId: target.caseId,
          expectedVersion: target.version,
          requestId: newRequestId(),
        });
        ownVersion.current.set(res.case.caseId, res.case.version);
        return true;
      } catch (err) {
        handleFailure(err, '取消');
        return false;
      } finally {
        endCase(target.caseId);
      }
    },
    [undoMutation, handleFailure, beginCase, endCase],
  );

  const value = useMemo<BoardValue>(
    () => ({
      cases: data.cases,
      settings: data.settings,
      degradedSince: data.degradedSince,
      updatedAt: data.updatedAt,
      hasSameSurname,
      act,
      undo,
      isBusy,
    }),
    [data, hasSameSurname, act, undo, isBusy],
  );

  return <BoardContext.Provider value={value}>{children}</BoardContext.Provider>;
}

export function useBoardData(): BoardValue {
  const ctx = useContext(BoardContext);
  if (!ctx) throw new Error('BoardProvider の外で useBoardData は使えません');
  return ctx;
}
